import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { supabase } from '../supabase';
import { getUser } from '../auth';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const createCustomIcon = (emoji) => L.divIcon({
  html: `<div style="background:white;border-radius:50%;width:40px;height:40px;display:flex;align-items:center;justify-content:center;font-size:1.2rem;box-shadow:0 4px 12px rgba(28,43,30,0.2);border:2px solid #3D9A6A">${emoji}</div>`,
  className: '',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const formatDist = (km) => km < 1 ? Math.round(km * 1000) + ' m' : km.toFixed(1) + ' km';

export default function CarteActivites() {
  const navigate = useNavigate();
  const user = getUser();
  const [activites, setActivites] = useState([]);
  const [selected, setSelected] = useState(null);
  const [userPos, setUserPos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState([45.7, 4.9]);

  useEffect(() => {
    fetchActivites();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => {
          setUserPos({ lat: pos.coords.latitude, lon: pos.coords.longitude });
          setCenter([pos.coords.latitude, pos.coords.longitude]);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  const fetchActivites = async () => {
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase
      .from('activites')
      .select('*, users(prenom, avatar_url)')
      .gte('date', today)
      .not('latitude', 'is', null)
      .not('longitude', 'is', null);

    if (data) {
      const avecInfos = await Promise.all(data.map(async a => {
        const { count } = await supabase.from('participations').select('*', { count: 'exact', head: true }).eq('activite_id', a.id);
        return { ...a, nb_participants: count || 0 };
      }));
      setActivites(avecInfos);
    }
    setLoading(false);
  };

  const activitesAvecDist = activites.map(a => ({
    ...a,
    dist: userPos ? getDistance(userPos.lat, userPos.lon, a.latitude, a.longitude) : null,
  })).sort((a, b) => {
    if (a.dist !== null && b.dist !== null) return a.dist - b.dist;
    return new Date(a.date) - new Date(b.date);
  });

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAF7', paddingBottom: 100 }}>

      {/* HEADER */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'white', borderBottom: '1px solid #EEF2EE', padding: '16px 20px', boxShadow: '0 2px 16px rgba(28,43,30,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', color: '#1C2B1E' }}>🗺️ Carte</div>
          <div style={{ fontSize: '0.72rem', color: '#8A9E8D', marginTop: 2 }}>
            {activites.length} activité{activites.length > 1 ? 's' : ''} à venir
          </div>
        </div>
        <div onClick={() => navigate('/feed')} style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.4rem', cursor: 'pointer' }}>
          <span style={{ color: '#3D9A6A' }}>Woo</span><span style={{ color: '#FF7043' }}>ki</span>
        </div>
      </div>

      {/* CARTE */}
      <div style={{ height: 340, position: 'relative' }}>
        {loading ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#EDF7F1' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>🗺️</div>
              <div style={{ fontFamily: 'Fredoka One, cursive', color: '#3D9A6A' }}>Chargement...</div>
            </div>
          </div>
        ) : (
          <MapContainer center={center} zoom={11} style={{ height: '100%', width: '100%' }} zoomControl={false}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
            />
            {userPos && (
              <Marker
                position={[userPos.lat, userPos.lon]}
                icon={L.divIcon({
                  html: `<div style="background:#3D9A6A;border-radius:50%;width:16px;height:16px;border:3px solid white;box-shadow:0 2px 8px rgba(61,154,106,0.5)"></div>`,
                  className: '',
                  iconSize: [16, 16],
                  iconAnchor: [8, 8],
                })}
              />
            )}
            {activitesAvecDist.map(a => (
              <Marker
                key={a.id}
                position={[a.latitude, a.longitude]}
                icon={createCustomIcon(a.emoji)}
                eventHandlers={{ click: () => setSelected(a) }}
              >
                <Popup>
                  <div style={{ fontFamily: 'Nunito, sans-serif', minWidth: 160 }}>
                    <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: 4 }}>{a.titre}</div>
                    <div style={{ fontSize: '0.78rem', color: '#8A9E8D', marginBottom: 8 }}>
                      {new Date(a.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                      {a.dist !== null && ` · ${formatDist(a.dist)}`}
                    </div>
                    <button onClick={() => navigate(`/detail/${a.id}`)} style={{ width: '100%', padding: '8px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer' }}>
                      Voir →
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>

      {/* LISTE SOUS LA CARTE */}
      <div style={{ padding: '16px 20px' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#8A9E8D', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
          {userPos ? 'Activités les plus proches' : 'Activités à venir'}
        </div>

        {activitesAvecDist.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>🌱</div>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: '#1C2B1E', marginBottom: 8 }}>Pas encore d'activités !</div>
            <button onClick={() => navigate('/creer')} style={{ padding: '13px 28px', background: '#FF7043', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer' }}>
              ＋ Créer une activité
            </button>
          </div>
        )}

        {activitesAvecDist.map(a => {
          const isSelected = selected?.id === a.id;
          const complet = a.nb_participants >= a.max_familles;
          const isToday = new Date(a.date).toDateString() === new Date().toDateString();
          const isTomorrow = new Date(a.date).toDateString() === new Date(Date.now() + 86400000).toDateString();

          return (
            <div key={a.id}
              onClick={() => { setSelected(a); navigate(`/detail/${a.id}`); }}
              style={{
                background: 'white',
                borderRadius: 20,
                padding: '14px 16px',
                marginBottom: 10,
                boxShadow: isSelected ? '0 6px 24px rgba(61,154,106,0.2)' : '0 4px 16px rgba(28,43,30,0.07)',
                cursor: 'pointer',
                border: isSelected ? '2px solid #3D9A6A' : '2px solid transparent',
                transition: 'all 0.2s',
                display: 'flex',
                gap: 14,
                alignItems: 'center',
              }}>
              <div style={{ width: 52, height: 52, borderRadius: 16, background: '#EDF7F1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', flexShrink: 0 }}>
                {a.emoji}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                  <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.95rem', color: '#1C2B1E' }}>{a.titre}</div>
                  {isToday && <span style={{ background: '#FFF3EE', color: '#FF7043', padding: '2px 8px', borderRadius: 100, fontSize: '0.62rem', fontWeight: 800 }}>Aujourd'hui</span>}
                  {isTomorrow && <span style={{ background: '#EDF7F1', color: '#3D9A6A', padding: '2px 8px', borderRadius: 100, fontSize: '0.62rem', fontWeight: 800 }}>Demain</span>}
                  {complet && <span style={{ background: '#FFF3EE', color: '#FF7043', padding: '2px 8px', borderRadius: 100, fontSize: '0.62rem', fontWeight: 800 }}>Complet</span>}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#8A9E8D', marginBottom: 6 }}>
                  {a.users?.prenom} · {new Date(a.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} · {a.heure?.slice(0,5)}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {a.dist !== null && (
                    <span style={{ background: '#EDF7F1', color: '#3D9A6A', padding: '3px 10px', borderRadius: 100, fontSize: '0.68rem', fontWeight: 800 }}>
                      📍 {formatDist(a.dist)}
                    </span>
                  )}
                  <span style={{ background: '#F3F4F2', color: '#8A9E8D', padding: '3px 10px', borderRadius: 100, fontSize: '0.68rem', fontWeight: 800 }}>
                    👨‍👩‍👧 {a.nb_participants}/{a.max_familles}
                  </span>
                  <span style={{ background: '#F3F4F2', color: '#8A9E8D', padding: '3px 10px', borderRadius: 100, fontSize: '0.68rem', fontWeight: 800 }}>
                    👶 {a.age_min}–{a.age_max} ans
                  </span>
                </div>
              </div>
              <div style={{ color: '#3D9A6A', fontSize: '1rem', flexShrink: 0 }}>→</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}